import {
  Autocomplete,
  Box,
  Checkbox,
  ClickAwayListener,
  FormControlLabel,
  InputBase,
  Popper,
  styled,
  autocompleteClasses,
} from '@mui/material'
import {
  Done as DoneIcon,
  Close as CloseIcon,
  ArrowDropDown,
} from '@mui/icons-material'
import React, { useEffect } from 'react'

interface MultiSelectDropdownProps {
  data: string[]
  label: string
  selectedValues?: string[]
  onChange?: (newValues: string[]) => void
}

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: `1px solid ${
        theme.palette.mode === 'light' ? '#eaecef' : '#30363d'
      }`,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

const StyledPopper = styled(Popper)(({ theme }) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: 250,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}))

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: 10,
  width: '100%',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
  '& input': {
    borderRadius: 4,
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
    padding: 8,
    border: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
    fontSize: 14,
    '&:focus': {
      boxShadow: `0 0 0 3px ${
        theme.palette.mode === 'light'
          ? 'rgba(3, 102, 214, 0.3)'
          : 'rgb(12, 45, 107)'
      }`,
      borderColor: theme.palette.mode === 'light' ? '#0366d6' : '#388bfd',
    },
  },
}))

const PopperComponent = (props: any) => <StyledAutocompletePopper {...props} />

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  data,
  label,
  selectedValues = [],
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [pendingValue, setPendingValue] =
    React.useState<string[]>(selectedValues)

  useEffect(() => {
    setPendingValue([...selectedValues])
  }, [selectedValues])

  const open = Boolean(anchorEl)
  const id = open ? `dropdown-${label}` : undefined

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    if (anchorEl) anchorEl.focus()
    setAnchorEl(null)
    onChange?.(pendingValue)
  }

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Select/deselct checkbox handler.
    const selectAll = event.target.checked ? [...data] : []
    setPendingValue(selectAll)
    onChange?.(selectAll)
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          fontSize: 20,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          border: '2px solid lightgrey',
          borderRadius: '4px',
        }}
      >
        <Box
          component="button"
          onClick={handleClick}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            width: '100%',
          }}
        >
          <span>
            {label.length > 15 ? `${label.slice(0, 15)}...` : `${label}`}
          </span>
          <ArrowDropDown />
        </Box>
      </Box>
      <StyledPopper
        id={id}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Box
              sx={(theme) => ({
                borderBottom: `1px solid ${theme.palette.primary.main}`,
                padding: '8px 10px',
                fontWeight: 600,
              })}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={pendingValue.length === data.length}
                    indeterminate={
                      pendingValue.length > 0 &&
                      pendingValue.length < data.length
                    }
                    onChange={handleCheckbox}
                  />
                }
                label="Select all"
              />
            </Box>
            <Autocomplete
              open
              multiple
              disableCloseOnSelect
              clearOnBlur={false}
              value={pendingValue}
              onChange={(event, newValue) => {
                setPendingValue(newValue)
                onChange?.(newValue)
              }}
              slots={{ popper: PopperComponent }}
              noOptionsText="No labels"
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Box
                    component={DoneIcon}
                    sx={{ width: 17, height: 17, mr: '5px', ml: '-2px' }}
                    style={{ visibility: selected ? 'visible' : 'hidden' }}
                  />
                  <Box sx={{ flexGrow: 1 }}>{option}</Box>
                  <Box
                    component={CloseIcon}
                    sx={{ opacity: 0.6, width: 18, height: 18 }}
                    style={{ visibility: selected ? 'visible' : 'hidden' }}
                  />
                </li>
              )}
              options={[...data]}
              getOptionLabel={(option) => option}
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  placeholder="Filter"
                />
              )}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </>
  )
}
